import type { AsyncModal, ClosedAsyncModal, OpenAsyncModal, Controls, Configuration } from './types';
import * as actions from './actions';
import { deepEqualByValue } from '~/util/objects';

type SetShowState<T> = React.Dispatch<React.SetStateAction<InternalHookState<T> | null>>;

/**
 * This is the hook's internal state. The hook only manages state while the modal is open. Otherwise
 * its state is null, and `modal.showing` is `false`.
 */
export interface InternalHookState<T> {
  /** The configuration provided by the user when `show()` was invoked. */
  configuration: Configuration<T>;

  /**
   * The local state of the model being edited. It will be sent to saveHandler. It is initialized
   * when invoking `show(...)` and nulled out when the modal is closed. It may be sparse at any
   * time (see `Configuration.isModelComplete`).
   */
  localModel?: Partial<T>;

  /** True if the model is currently attempting to save. */
  isSaving: boolean;

  /**
   * True if saving was successfully completed. By default this closes the model automatically, in
   * which case you will not see this being true. But if you modify close behavior to remain open
   * after saving, you can use this for messaging.
   */
  saveSucceeded: boolean;

  /** True if the model is currently loading. */
  loading: boolean;

  /** If saving failed, this will be the resulting error. */
  serverError: null | string | React.ReactNode;
}

/**
 * This is the default implementation of `isModelComplete`, which the hook uses to determine if
 * `canSave` is true. This implementation is the absolute minimum needed by the hook, but will not
 * lead to a good user experience.
 */
export function defaultIsModelComplete<T>( _model?: Partial<T> ): _model is T {
  return _model !== undefined;
}

/**
 * This will either get SHOWING hooks or NOT SHOWING hooks, depending on current state.
 */
export function getHook<T>( showState: InternalHookState<T> | null, setShowState: SetShowState<T> ): AsyncModal<T> {
  return showState === null ? getClosedHook( setShowState ) : getOpenHook( setShowState, showState );
}

/**
 * This populates the hook state when the modal is not showing. This is simply an indication that we
 * are not showing, and a function to show.
 */
export function getClosedHook<T>( setShowState: SetShowState<T> ): ClosedAsyncModal<T> {
  return {
    showing: false,
    show: actions.show( setShowState ),
  };
}

/**
 * This populates the user-facing state when the modal is showing. This is a combination of state
 * and actions.
 */
export function getOpenHook<T>( setShowState: SetShowState<T>, showState: InternalHookState<T> ): OpenAsyncModal<T> {
  const {
    localModel,
    configuration: { compareModels = deepEqualByValue, isNew = false, isModelComplete = defaultIsModelComplete },
  } = showState;

  const isDirty = !compareModels( showState.configuration.initialModel, showState.localModel );
  const modelIsComplete = isModelComplete( localModel );
  const controls: Controls<T> = {
    configuration: showState.configuration,
    localModel,
    initialModel: showState.configuration.initialModel,
    isNew,
    isDirty,
    canSave: modelIsComplete && ( isDirty || isNew ),
    isSaving: showState.isSaving,
    saveSucceeded: showState.saveSucceeded,
    loading: showState.loading,
    serverError: showState.serverError,
    close: actions.closeModal( setShowState, showState, isDirty ),
    restart: actions.restart( setShowState, showState ),
    save: actions.save( setShowState, showState, isModelComplete ),
    update: actions.update( setShowState, showState ),
    updateField: actions.updateField( setShowState, showState ),
  };

  return {
    showing: true,
    show: actions.show( setShowState ),
    controls,
  };
}
